import styled from '@emotion/styled';
import {
  BrowserMessageType,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {isRouteErrorResponse, useRouteError} from 'react-router-dom';
import {
  EAccordion,
  EAccordionDetails,
  EAccordionSummary,
  EButton,
} from 'webapp-lib/Components';
import {mixinFlexCenter} from 'webapp-lib/styling';

import {ErrorScreen} from '_/assets/images';
import {NOT_FOUND} from '_/utilities/string';

export const GlobalErrorBoundry = (): React.ReactElement => {
  const {t} = useTranslation('common');
  const error = useRouteError();

  const resetAndReload = (includeCookies: boolean) => {
    sendElectronMessage({
      payload: {includeCookies},
      type: BrowserMessageType.CLEAR_ELECTRON_CACHE,
    });

    localStorage.clear();
    window.location.href = '/';
  };

  const softReset = () => {
    resetAndReload(false);
  };

  const hardReset = () => {
    // clearing the cookies will logout the user
    resetAndReload(true);
  };

  const errorInfos = getErrorMessage(error);

  return (
    <PageWrapper>
      <CenterWrapper>
        <ImageWrapper>
          <ErrorScreen />
          <Divider />
        </ImageWrapper>
        <h1>{t('errorBoundry.global.title')}</h1>
        <p>{t('errorBoundry.global.errorExplanation')}</p>
        <EButton onClick={softReset}>
          {t('errorBoundry.global.refreshButtonText')}
        </EButton>
        <p>{t('errorBoundry.global.resetExplanation')}</p>
        <EButton onClick={hardReset}>
          {t('errorBoundry.global.resetButtonText')}
        </EButton>
        {process.env.NODE_ENV === 'development' ? (
          <EAccordion defaultExpanded>
            <EAccordionSummary>Error details:</EAccordionSummary>
            <EAccordionDetails>
              <ErrorWrapper>{errorInfos.title}</ErrorWrapper>
              <ErrorWrapper>{errorInfos.details}</ErrorWrapper>
            </EAccordionDetails>
          </EAccordion>
        ) : undefined}
      </CenterWrapper>
    </PageWrapper>
  );
};

const getErrorMessage = (error: unknown): {title: string; details: string} => {
  if (isRouteErrorResponse(error)) {
    return {title: error.statusText, details: error.data ?? NOT_FOUND};
  } else if (error instanceof Error) {
    return {title: error.message, details: error.stack || error.name};
  } else if (typeof error === 'string') {
    return {title: error, details: NOT_FOUND};
  }

  return {title: 'Unknown error', details: NOT_FOUND};
};

const ImageWrapper = styled.div`
  ${mixinFlexCenter}
  flex-direction: column;
  height: min-content;
  width: 100%;

  > * {
    width: 50%;
    min-width: 400px;
    max-width: 1100px;
    color: var(--global-error-icon-color);
  }
`;

const CenterWrapper = styled.div`
  ${mixinFlexCenter}
  flex-direction: column;
  gap: var(--gutter-large);
  padding: var(--gutter-big);
  width: 50%;
  text-align: center;
  line-height: 1.75;
  font-size: 18px;
`;

const PageWrapper = styled.div`
  width: 100%;
  ${mixinFlexCenter}
  min-height: 100vh;
`;

const Divider = styled.hr`
  border: 2px solid var(--color-core-primary);
  margin: 0px;
`;

const ErrorWrapper = styled.div`
  color: var(--color-alerts-error);
  text-align: left;
  white-space: pre;
`;
